import React, { useEffect, useRef, useState } from "react";
import "../css/career.css";
import "../css/careerSl.css";
import "../css/careerSt.css";
import "../css/careerSm.css";
import { ArrowRightIcon } from "../../../../assets-module/asset-configuration/icon/iconExporter/iconExporter";
import HeroContent from "../../../../component-module/component-services/title/hero/ts/heroContent";
import SectionTitle from "../../../../component-module/component-services/title/section/ts/sectionTitle";
import JobRoleList from "../../../../component-module/component-services/list/job-role/ts/jobRoleList";
import RoundButton from "../../../../component-module/component-services/button/round-button/ts/roundButton";
import TalentList from "../../../../component-module/component-services/list/talents/ts/talent";
import EmptyPosition from "../../../../component-module/component-services/card/job-filter/empty-job/ts/emptyPosition";
import { useNavigate } from "react-router-dom";
import CareerGradient from "./careerGradient";
import { CareerPageData, mapStepsWithApplyButton, getJobIndexApi, mapJobIndexToJobRoles } from "./career.type";
import { useCmsPage } from "../../../../context-module/context-configuration/auth-provider/appShell/appShellContent/appUseCntData";
import { API_ENDPOINTS } from "../../../../context-module/context-configuration/auth-provider/apiGroup/apiProvider";

import type { JobRole } from "../../../../component-module/component-services/list/job-role/ts/jobRole.type";

const Career = () => {
  const navigate = useNavigate();
  const submitFormRef = useRef<HTMLDivElement | null>(null);

  const { data } = useCmsPage<CareerPageData>(API_ENDPOINTS.ROUTE_URL.CAREER);

  const careerData = data?.career?.cta?.[0];
  const hero = careerData?.heroSection;
  const positionSection = careerData?.positionSection;
  const submissionStepSection = careerData?.submissionStepSection;

  const submissionContent = submissionStepSection?.content[0];
  const positionContent = positionSection?.content?.[0];

  const [jobRoles, setJobRoles] = useState<JobRole[]>([]);
  const [jobLoading, setJobLoading] = useState(false);
  const [jobError, setJobError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;

    const run = async () => {
      try {
        setJobLoading(true);
        setJobError(null);

        const res = await getJobIndexApi(1, 6);
        if (!alive) return;

        const items = res.data.data.items ?? /* istanbul ignore next */[];
        setJobRoles(mapJobIndexToJobRoles(items));
      } catch (e: any) {
        if (!alive) return;
        setJobError(e?.message ?? /* istanbul ignore next */  "Failed to load jobs");
        setJobRoles([]);
      } finally {
        if (!alive) return; //NOSONAR
        setJobLoading(false);
      }
    };

    run();
    return () => {
      alive = false;
    };
  }, []);

  const onClickApplyNow = () => {
    submitFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const onClickViewAllPos = () => {
    navigate("position/all");
  };

  const rawSteps = submissionContent?.headerSection?.list?.items ?? [];
  const steps = mapStepsWithApplyButton(rawSteps, onClickApplyNow);

  const hasJobs = jobRoles.length > 0;

  let jobListContent: React.ReactNode;

  if (jobLoading) {
    jobListContent = <EmptyPosition />;
  } else if (jobError) {
    jobListContent = <EmptyPosition />;
  } else if (hasJobs) {
    jobListContent = <JobRoleList jobs={jobRoles} variant="borderless" />;
  } else {
    jobListContent = <EmptyPosition />;
  }

  return (
    <div>
      <section className="scl--web-page-career-hero-section">
        <div className="scl--wpchs-cont-stl-bo">
          {hero?.visible && (
            <HeroContent
              titleLine1={hero?.headline}
              buttonText={hero?.primaryButton?.label}
              buttonIconPosition="right"
              onButtonClick={onClickApplyNow}
              btnVisible={hero?.primaryButton?.visible}
            />
          )}
        </div>
        <div className="scl--wpchs-cont-stl-bo-gradient">
          <CareerGradient />
        </div>
      </section>

      {positionSection?.visible && (
        <section className="scl--web-page-career-join-us-section" ref={submitFormRef}>
          <div className="scl--wpcjus-section-job-seek">
            {positionContent?.headerSection?.visible && (
              <div className="scl--web-page-career-join-us-title-side">
                <SectionTitle
                  label={positionContent?.headerSection?.label}
                  title={positionContent?.headerSection?.title}
                  className="scl--web-page-career-join-us-section-title"
                />

                {positionContent?.headerSection?.actionButton?.visible && (
                  <RoundButton
                    text={positionContent?.headerSection?.actionButton?.label}
                    icon={<ArrowRightIcon />}
                    iconPosition="right"
                    onClick={onClickViewAllPos}
                    className="scl--web-page-career-view-all-button"
                  />
                )}
              </div>
            )}

            <div
              className={`scl--web-page-career-join-us-job-list-side ${jobRoles.length > 0 ? "" : "active"
                }`}
            >
              {jobListContent}
            </div>
          </div>
        </section>
      )}

      {submissionStepSection?.visible && (
        <section className="scl--web-page-career-our-talent-section">
          <div className="scl--web-page-career-our-talent-content">
            <SectionTitle
              label={submissionContent?.headerSection?.label}
              title={submissionContent?.headerSection?.title}
              description={submissionContent?.headerSection?.description}
              className="scl--web-page-career-our-talent-section-title"
            />
            <TalentList steps={steps} />
          </div>
        </section>
      )}
    </div>
  );
};

export default Career;